import { useEffect, useState } from "react"
import { useNavigate, useParams } from "react-router"
import type { Server } from "@mc/types"
import { getServer, updateServer, deleteServer } from "../api/servers.js"
import { friendlyError } from "../api/errorMessages.js"
import { Spinner } from "../components/ui/Spinner.js"
import { Button } from "../components/ui/Button.js"
import { Input } from "../components/ui/Input.js"
import { useToast } from "../components/ui/Toast.js"

type PageState = { status: "loading" } | { status: "ready"; server: Server } | { status: "error"; message: string }

/**
 * `/servers/[id]/settings` (FR section 8/19 item 13, FR section 11). Only
 * the fields that live in the `servers` table: name, description, icon,
 * public IP. The rest of FR section 11 (MC version, RAM, gamemode,
 * difficulty, whitelist) lives in Crafty's server.properties and needs
 * the synchronous Gateway<->server-manager mechanism AGENT.md flags as
 * pending Architect design (see PATCH /api/v1/servers/:serverId's own
 * comment) — not built here, not faked.
 */
export function ServerSettingsPage() {
  const { serverId } = useParams<{ serverId: string }>()
  const navigate = useNavigate()
  const [state, setState] = useState<PageState>({ status: "loading" })
  const [form, setForm] = useState({ name: "", description: "", iconUrl: "", publicIp: "" })
  const [saving, setSaving] = useState(false)
  const [deleteConfirmText, setDeleteConfirmText] = useState("")
  const [deleting, setDeleting] = useState(false)
  const toast = useToast()

  useEffect(() => {
    if (!serverId) return
    let cancelled = false
    getServer(serverId)
      .then(({ server }) => {
        if (cancelled) return
        setState({ status: "ready", server })
        setForm({
          name: server.name,
          description: server.description ?? "",
          iconUrl: server.iconUrl ?? "",
          publicIp: server.publicIp ?? "",
        })
      })
      .catch((err) => {
        if (!cancelled) setState({ status: "error", message: friendlyError(err).message })
      })
    return () => {
      cancelled = true
    }
  }, [serverId])

  if (!serverId) return null

  if (state.status === "loading") {
    return (
      <div style={{ display: "grid", placeItems: "center", padding: "var(--space-12) 0" }}>
        <Spinner size="lg" />
      </div>
    )
  }

  if (state.status === "error") {
    return (
      <p role="alert" style={{ color: "var(--status-error)", fontSize: "var(--text-sm)" }}>
        {state.message}
      </p>
    )
  }

  const dirty =
    form.name !== state.server.name ||
    form.description !== (state.server.description ?? "") ||
    form.iconUrl !== (state.server.iconUrl ?? "") ||
    form.publicIp !== (state.server.publicIp ?? "")

  const handleSave = async () => {
    setSaving(true)
    try {
      const { server } = await updateServer(serverId, {
        name: form.name,
        description: form.description || null,
        iconUrl: form.iconUrl || null,
        publicIp: form.publicIp || null,
      })
      setState({ status: "ready", server })
      toast.show("Ajustes guardados.", "success")
    } catch (err) {
      toast.show(friendlyError(err).message, "error")
    } finally {
      setSaving(false)
    }
  }

  /**
   * Borrado del servidor (Danger Zone). Friccion deliberadamente MAYOR que
   * el patron `toast.confirmDestructive` (undo automatico a los 3s) que
   * usa el resto de la app para start/stop/restart/kill (ver
   * ServerOverviewPage.tsx): esas acciones son reversibles (un servidor
   * detenido se puede volver a iniciar), esta no lo es -- borra el
   * servidor de Crafty de verdad (CraftyClient.deleteServer(), ver
   * queryHandlers.ts), sin ningun mecanismo de deshacer posible una vez
   * confirmado. Exigir que el usuario tipee el nombre exacto del servidor
   * (mismo patron que GitHub/Supabase/etc usan para borrados irreversibles)
   * es la fila de confirmacion real; el toast.confirmDestructive despues
   * es solo el paso final consistente con el resto de la UI, no la unica
   * proteccion.
   */
  const handleDelete = () => {
    if (deleteConfirmText !== state.server.name) return
    toast.confirmDestructive(
      `Se va a borrar "${state.server.name}" de forma permanente, incluyendo su instancia en Crafty. Esta acción no se puede deshacer.`,
      async () => {
        setDeleting(true)
        try {
          const result = await deleteServer(serverId)
          if (result.error) {
            // deleted:true + error no-null: Crafty ya borro de verdad (ver
            // deleteServer()'s doc en api/servers.ts), el registro local tuvo
            // un problema aparte -- se informa como advertencia, no como
            // fallo total, y se navega igual porque el servidor ya no existe
            // en Crafty ni es administrable desde aca.
            toast.show(`Servidor borrado en Crafty, pero hubo un problema al limpiar el registro local: ${result.error}`, "error")
          } else {
            toast.show("Servidor borrado.", "success")
          }
          navigate("/servers")
        } catch (err) {
          toast.show(friendlyError(err).message, "error")
          setDeleting(false)
        }
      },
      () => toast.show("Borrado cancelado.", "info"),
    )
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-6)", maxWidth: 480 }}>
      <div>
        <h1 style={{ fontSize: "var(--text-3xl)" }}>Ajustes</h1>
        <p style={{ color: "var(--text-muted)" }}>Nombre, descripción, ícono e IP pública de este servidor.</p>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-4)" }}>
        <Input label="Nombre" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        <Input
          label="Descripción"
          value={form.description}
          onChange={(e) => setForm({ ...form, description: e.target.value })}
        />
        <Input
          label="URL del ícono"
          value={form.iconUrl}
          onChange={(e) => setForm({ ...form, iconUrl: e.target.value })}
        />
        <Input
          label="IP pública"
          value={form.publicIp}
          onChange={(e) => setForm({ ...form, publicIp: e.target.value })}
        />
      </div>

      <Button loading={saving} disabled={!dirty || !form.name} onClick={handleSave} style={{ alignSelf: "flex-start" }}>
        Guardar cambios
      </Button>

      <div
        style={{
          marginTop: "var(--space-6)",
          padding: "var(--space-4)",
          borderRadius: "var(--radius-md)",
          border: "1px solid var(--status-error)",
          display: "flex",
          flexDirection: "column",
          gap: "var(--space-3)",
        }}
      >
        <div>
          <h2 style={{ fontSize: "var(--text-lg)", color: "var(--status-error)" }}>Zona de peligro</h2>
          <p style={{ color: "var(--text-muted)", fontSize: "var(--text-sm)" }}>
            Borrar este servidor lo elimina también de Crafty de forma permanente. No se puede deshacer.
          </p>
        </div>
        <Input
          label={`Escribí "${state.server.name}" para confirmar`}
          value={deleteConfirmText}
          onChange={(e) => setDeleteConfirmText(e.target.value)}
          autoComplete="off"
        />
        <Button
          variant="destructive"
          loading={deleting}
          disabled={deleteConfirmText !== state.server.name}
          onClick={handleDelete}
          style={{ alignSelf: "flex-start" }}
        >
          Borrar servidor
        </Button>
      </div>
    </div>
  )
}
